import RoleAssignments from "../components/RoleAssignments";
import { personScopeUrl } from "../components/personScope";
import { formatStreckeQuantity } from "../components/streckeMappings";
import React, { useEffect, useRef, useState } from "react";
import api from "../api/axios";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, IconButton, Box, Typography, Collapse, Tooltip } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import Add from "@mui/icons-material/Add";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import GpsFixed from "@mui/icons-material/GpsFixed";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import { JaegerIcon, KundeIcon } from "../components/icons/JagdverwaltungIcons";
import RecordLink from "../components/RecordLink";
import { useSelectedRecord } from "../components/useRecordTarget";
import PersonDialog from "../components/PersonDialog";
import BestellungDialog from "../components/BestellungDialog";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';
import dayjs from "dayjs";
import API_BASE_URL from "../config";
import { useSearchParams } from "react-router-dom";
import { useBusinessContext } from "../BusinessContext";


const Personen = ({ modus: propModus }) => {
  const { withContext, mandantId, betriebId, ready } = useBusinessContext();
  const [roleDialog, setRoleDialog] = useState(null);
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personen, setPersonen] = useState([]);
  const [personenLoaded, setPersonenLoaded] = useState(false);
  const [personenError, setPersonenError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [activePerson, setActivePerson] = useState(null);
  const [expandedPurchases, setExpandedPurchases] = useState(null);
  const [purchaseData, setPurchaseData] = useState({});
  const [expandedOrders, setExpandedOrders] = useState(null);
  const [orderData, setOrderData] = useState({});
  const [orderDialogCustomer, setOrderDialogCustomer] = useState(null);
  const [detailStatus, setDetailStatus] = useState({});
  const detailGeneration = useRef(0);

  // Query-Parameter aus URL lesen (z.B. /personen?modus=kunde)
  const [searchParams] = useSearchParams();
  const queryModus = searchParams.get("modus");
  const normalizeModus = (m) => {
    if (!m) return "all";
    const mm = m.toString().toLowerCase();
    if (mm === "kunden" || mm === "kunde") return "kunden";
    if (mm === "jaeger" || mm === "jäger") return "jaeger";
    return "all";
  };
  const modus = normalizeModus(queryModus || propModus);
  const { selected, targetRef } = useSelectedRecord(personen, "Id", setActivePerson);

  const streckeUrl = `${API_BASE_URL}/strecke`;
  const verkaufUrl = `${API_BASE_URL}/verkauf`;
  const personenUrl = `${API_BASE_URL}/personen`;
  // 🔄 Personen aus Backend laden
  useEffect(() => {
    let cancelled = false;
    detailGeneration.current += 1;
    setDetailStatus({});
    setPersonen([]);
    setPersonenLoaded(false);
    setPersonenError(false);
    setActivePerson(null);
    setExpandedPurchases(null);
    setExpandedOrders(null);
    setPurchaseData({});
    setOrderData({});
    if (!mandantId || (modus !== "all" && !betriebId)) return () => { cancelled = true; };
    let url = personScopeUrl(personenUrl, mandantId);
    if (modus === "kunden") { url = personScopeUrl(`${personenUrl}?filter=kunden&aktiv=true`, mandantId, betriebId); if (ready) url = withContext(url); }
    if (modus === "jaeger") url = personScopeUrl(`${personenUrl}?filter=jaeger&aktiv=true`, mandantId, betriebId);
    console.log("Lade Personen mit Modus:", modus, "von URL:", url);
    api.get(url)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        if (!cancelled) {
          setPersonen(rows);
          setPersonenLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setPersonenError(true); });
    return () => { cancelled = true; };
  }, [modus, personenUrl, selected, withContext, mandantId, betriebId, ready, revision]);

  // ✨ Dialog öffnen (zum Bearbeiten oder Erstellen)
  const handleOpen = (person = null) => {
    setSelectedPerson(person);
    setOpen(true);
  };

  // 💾 Person speichern (neu oder aktualisieren)
  const handleSave = async (personData) => {
    try {
      if (selectedPerson) {
        console.log("Aktualisiere Person:", selectedPerson.Id, personData);
        await api.put(personScopeUrl(`${personenUrl}/${selectedPerson.Id}`, mandantId), personData);
        const updatedPerson = { ...selectedPerson, ...personData };
        setPersonen((current) => current.map((person) => (
          person.Id === selectedPerson.Id ? updatedPerson : person
        )));
        setActivePerson((current) => (
          current?.Id === selectedPerson.Id ? updatedPerson : current
        ));
        triggerSnackbar("Person erfolgreich aktualisiert!", "success");
      } else {
        const response = await api.post(personScopeUrl(personenUrl, mandantId), personData);
        setPersonen((current) => [...current, response.data]);
        triggerSnackbar("Person erfolgreich hinzugefügt!", "success");
      }
      setOpen(false);
      setSelectedPerson(null);
    } catch {
      triggerSnackbar(
        selectedPerson ? "Fehler beim Aktualisieren!" : "Fehler beim Hinzufügen!",
        "error"
      );
    }
  };

  // 🗑 Person löschen
  const handleDelete = (id) => {
    api.delete(personScopeUrl(`${personenUrl}/${id}`, mandantId))
      .then(() => {
        setPersonen(personen.filter((person) => person.Id !== id));
        triggerSnackbar("Person erfolgreich gelöscht!", "success");
        if (activePerson && activePerson.Id === id) setActivePerson(null);
      })
      .catch(() => triggerSnackbar("Fehler beim Löschen!", "error"));
  };

  // ✨ Snackbar auslösen
  const triggerSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadPurchaseData = (personId) => {
    if (modus !== 'jaeger' && modus !== 'kunden') return;
    const key = `purchase:${personId}`, generation = detailGeneration.current;
    setDetailStatus(previous => ({ ...previous, [key]: { loading: true } }));
    const url = modus === 'jaeger' ? `${streckeUrl}?jaeger_id=${personId}&all_years=true` : `${verkaufUrl}?kunde_id=${personId}&all_years=true`;
    api.get(personScopeUrl(url, mandantId, betriebId)).then(response => {
      if (generation !== detailGeneration.current) return;
      setPurchaseData(previous => ({ ...previous, [personId]: Array.isArray(response.data) ? response.data : [] }));
      setDetailStatus(previous => ({ ...previous, [key]: {} }));
    }).catch(() => { if (generation === detailGeneration.current) setDetailStatus(previous => ({ ...previous, [key]: { error: 'Einträge konnten nicht geladen werden.' } })); });
  };

  const handlePurchaseExpand = (personId) => {
    if (expandedPurchases === personId) {
      setExpandedPurchases(null);
    } else {
      setExpandedPurchases(personId);
      if (!purchaseData[personId]) {
        loadPurchaseData(personId);
      }
    }
  };

  const loadOrders = (personId) => {
    const key = `orders:${personId}`, generation = detailGeneration.current;
    setDetailStatus(previous => ({ ...previous, [key]: { loading: true } }));
    api.get(personScopeUrl(`/bestellungen?person_id=${personId}`, mandantId, betriebId))
      .then((response) => { if (generation !== detailGeneration.current) return; setOrderData((current) => ({
        ...current,
        [personId]: Array.isArray(response.data) ? response.data : [],
      })); setDetailStatus(previous => ({ ...previous, [key]: {} })); })
      .catch(() => {
        if (generation === detailGeneration.current) setDetailStatus(previous => ({ ...previous, [key]: { error: 'Bestellungen konnten nicht geladen werden.' } }));
      });
  };

  const handleOrderExpand = (personId) => {
    if (expandedOrders === personId) {
      setExpandedOrders(null);
    } else {
      setExpandedOrders(personId);
      if (!orderData[personId]) loadOrders(personId);
    }
  };

  const tableColumnCount = modus === "jaeger" ? 8 : modus === "kunden" ? 9 : 8;
  const formatDetailDate = (value) => value ? dayjs(value).format("DD.MM.YYYY") : "-";
  const formatCustomerRemark = (value) => {
    const characters = Array.from(value || "-");
    return characters.length > 24 ? `${characters.slice(0, 23).join("")}…` : characters.join("");
  };
  const formatLatestPurchase = (person) => {
    const date = person.LetzterEinkauf_Datum;
    if (!date || !dayjs(date).isValid()) return "-";
    const ageClass = person.LetzterEinkauf_Altersklasse?.trim();
    return [dayjs(date).format("DD.MM.YYYY"), ageClass].filter(Boolean).join(" · ");
  };

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box sx={{ flex: 1 }}>
        {/* Einheitliche Titelzeile mit direkt vorangestellter Aktion */}
        <PageHeader
          title={modus === "jaeger" ? "Jäger" : modus === "kunden" ? "Kunden" : "Personen"}
          action={<Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => handleOpen(null)}
          >
            Neu
          </Button>}
        />

        {modus !== "all" && <Button onClick={() => setRoleDialog({ role: modus === "jaeger" ? "jaeger" : "kunden" })}>Bestehende Person zuordnen</Button>}
        {mandantId && (modus === 'all' || betriebId) && !personenLoaded && !personenError && <LoadingIndicator label={modus === 'jaeger' ? 'Jäger werden geladen …' : modus === 'kunden' ? 'Kunden werden geladen …' : 'Personen werden geladen …'} />}
        {personenError && <LoadError onRetry={() => setRevision(value => value + 1)}>Die Personenliste konnte nicht geladen werden.</LoadError>}
        {selected && personenLoaded && !personen.some(person => String(person.Id) === selected) && (
          <Alert severity="warning">{modus === "jaeger"
            ? "Der verlinkte Jäger ist in dieser Ansicht nicht verfügbar."
            : "Der verlinkte Kunde ist in dieser Ansicht nicht verfügbar."}</Alert>
        )}
        <TableContainer component={Paper} style={{ width: "100%", overflow: "auto"}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>&nbsp;</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Vorname</TableCell>
                {modus !== "kunden" && <TableCell>Geburtstag</TableCell>}
                {modus === "jaeger" && <TableCell>Status</TableCell>}
                {modus === "kunden" && <TableCell>Status</TableCell>}
                {modus === "jaeger" && <TableCell>Bereich</TableCell>}
                <TableCell>Telefon 1</TableCell>
                {modus !== "jaeger" && <TableCell>Telefon 2</TableCell>}
                {modus === "all" && <TableCell>Mail</TableCell>}
                {modus === "kunden" && <TableCell>Bemerkung</TableCell>}
                {modus === "kunden" && <TableCell sx={{ whiteSpace: "nowrap" }}>Letzter Einkauf</TableCell>}
                <TableCell sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personen.map((person) => (
                <React.Fragment key={person.Id}>
                  <TableRow
                    hover
                    ref={String(person.Id) === selected ? targetRef : undefined}
                    selected={activePerson && activePerson.Id === person.Id}
                    onClick={() => setActivePerson(person)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ width: modus === "kunden" ? 80 : 40, whiteSpace: "nowrap" }}>
                      <Tooltip title={modus === "kunden"
                        ? (expandedPurchases === person.Id ? "Verkäufe ausblenden" : "Verkäufe anzeigen")
                        : (expandedPurchases === person.Id ? "Strecke ausblenden" : "Strecke anzeigen")}
                      >
                        <IconButton
                          size="small"
                          color={expandedPurchases === person.Id ? "primary" : "default"}
                          aria-label={modus === "kunden"
                            ? (expandedPurchases === person.Id ? "Käufe ausblenden" : "Käufe anzeigen")
                            : (expandedPurchases === person.Id ? "Strecke ausblenden" : "Strecke anzeigen")}
                          aria-expanded={expandedPurchases === person.Id}
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePurchaseExpand(person.Id);
                          }}
                        >
                          {modus === "kunden"
                            ? <ShoppingCart fontSize="small" />
                            : <GpsFixed fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      {modus === "kunden" && (
                        <Tooltip title="Bestellungen anzeigen">
                          <IconButton
                            size="small"
                            color={expandedOrders === person.Id ? "primary" : "default"}
                            aria-label="Bestellungen anzeigen"
                            aria-expanded={expandedOrders === person.Id}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOrderExpand(person.Id);
                            }}
                          >
                            <ReceiptLong fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{person.Typ === "ORGANIZATION" ? person.Organisationsname : person.Name}</TableCell>
                    <TableCell>{person.Typ === "ORGANIZATION" ? "Organisation" : person.Vorname}</TableCell>
                    {modus !== "kunden" && <TableCell>{person.Geburtsdatum ? dayjs(person.Geburtsdatum).format("DD.MM.YYYY") : ""}</TableCell>}
                    {modus === "jaeger" && <TableCell>{person.Status}</TableCell>}
                    {modus === "kunden" && <TableCell>{person.Aktiv ? "aktiv" : "inaktiv"}</TableCell>}
                    {modus === "jaeger" && <TableCell>{person.Bereich ?? "-"}</TableCell>}
                    <TableCell>{person.Telefon1 || "-"}</TableCell>
                    {modus !== "jaeger" && <TableCell>{person.Telefon2 || "-"}</TableCell>}
                    {modus === "all" && <TableCell>{person.Mail || "-"}</TableCell>}
                    {modus === "kunden" && (
                      <TableCell title={person.Bemerkungen || undefined} sx={{ whiteSpace: "pre-wrap" }}>
                        {formatCustomerRemark(person.Bemerkungen)}
                      </TableCell>
                    )}
                    {modus === "kunden" && (
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatLatestPurchase(person)}</TableCell>
                    )}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {modus === "all" && <>
                        <Tooltip title="Jägerzuordnung bearbeiten"><IconButton aria-label="Jägerzuordnung bearbeiten" disabled={person.Typ === "ORGANIZATION"} color="primary" size="small"
                          onClick={e => { e.stopPropagation(); setRoleDialog({ person, role: "jaeger" }); }}><JaegerIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Kundenzuordnung bearbeiten"><IconButton aria-label="Kundenzuordnung bearbeiten" color="primary" size="small"
                          onClick={e => { e.stopPropagation(); setRoleDialog({ person, role: "kunden" }); }}><KundeIcon fontSize="small" /></IconButton></Tooltip>
                      </>}
                      <Tooltip title={modus === "all" ? "Person bearbeiten" : modus === "jaeger" ? "Jägerzuordnung bearbeiten" : "Kundenzuordnung bearbeiten"}>
                      <IconButton aria-label={modus === "all" ? "Person bearbeiten" : modus === "jaeger" ? "Jägerzuordnung bearbeiten" : "Kundenzuordnung bearbeiten"}
                        color="primary" size="small" onClick={e => {
                          e.stopPropagation();
                          if (modus === "all") handleOpen(person);
                          else setRoleDialog({ person, role: modus === "jaeger" ? "jaeger" : "kunden" });
                        }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      </Tooltip>
                      {modus === "all" && <IconButton aria-label="Person löschen" color="error" onClick={e => { e.stopPropagation(); handleDelete(person.Id); }} size="small">
                        <Delete fontSize="small" />
                      </IconButton>}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={tableColumnCount}>
                      <Collapse in={expandedPurchases === person.Id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, ml: 4, bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                          {modus === "jaeger" ? (
                            <>
                              <Typography variant="subtitle1" gutterBottom>
                                Strecke des Jägers
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Wildart</TableCell>
                                    <TableCell>Altersklasse</TableCell>
                                    <TableCell>Geschlecht</TableCell>
                                    <TableCell>Menge</TableCell>
                                    <TableCell>Ort</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detailStatus[`purchase:${person.Id}`]?.loading || detailStatus[`purchase:${person.Id}`]?.error ? <TableRow><TableCell colSpan={6}>{detailStatus[`purchase:${person.Id}`]?.error ? <LoadError onRetry={() => loadPurchaseData(person.Id)}>{detailStatus[`purchase:${person.Id}`].error}</LoadError> : <LoadingIndicator label="Strecke wird geladen …" compact />}</TableCell></TableRow> : (purchaseData[person.Id] || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center">
                                        Keine Strecke vorhanden.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    (purchaseData[person.Id] || []).map((strecke) => (
                                      <TableRow key={strecke.Id}>
                                        <TableCell><RecordLink page="strecke" id={strecke.Id} betriebsjahrId={strecke.Betriebsjahr_Id}>{strecke.Datum ? dayjs(strecke.Datum).format("DD.MM.YYYY") : ""}</RecordLink></TableCell>
                                        <TableCell><RecordLink page="strecke" id={strecke.Id} betriebsjahrId={strecke.Betriebsjahr_Id}>{strecke.Wildname}</RecordLink></TableCell>
                                        <TableCell><RecordLink page="strecke" id={strecke.Id} betriebsjahrId={strecke.Betriebsjahr_Id}>{strecke.Altersklasse}</RecordLink></TableCell>
                                        <TableCell>{strecke.Geschlecht}</TableCell>
                                        <TableCell>
                                          {
                                            formatStreckeQuantity(strecke)
                                          }
                                        </TableCell>
                                        <TableCell>{strecke.Ort}</TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </>
                          ) : (
                            <>
                              <Typography variant="subtitle1" gutterBottom>
                                Verkäufe an diesen Kunden ({(purchaseData[person.Id] || []).length})
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Wild</TableCell>
                                    <TableCell>Menge</TableCell>
                                    <TableCell>Preis</TableCell>
                                    <TableCell>Betrag</TableCell>
                                    <TableCell>Bezahlt</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detailStatus[`purchase:${person.Id}`]?.loading || detailStatus[`purchase:${person.Id}`]?.error ? <TableRow><TableCell colSpan={6}>{detailStatus[`purchase:${person.Id}`]?.error ? <LoadError onRetry={() => loadPurchaseData(person.Id)}>{detailStatus[`purchase:${person.Id}`].error}</LoadError> : <LoadingIndicator label="Verkäufe werden geladen …" compact />}</TableCell></TableRow> : (purchaseData[person.Id] || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center">
                                        Keine Verkäufe vorhanden.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    (purchaseData[person.Id] || []).map((verkauf) => (
                                      <TableRow key={verkauf.v_id}>
                                        <TableCell><RecordLink page="verkauf" id={verkauf.v_id} betriebsjahrId={verkauf.s_betriebsjahr_id}>{verkauf.v_datum ? dayjs(verkauf.v_datum).format("DD.MM.YYYY") : ""}</RecordLink></TableCell>
                                        <TableCell><RecordLink page="verkauf" id={verkauf.v_id} betriebsjahrId={verkauf.s_betriebsjahr_id}>{verkauf.w_altersklasse}</RecordLink></TableCell>
                                        <TableCell>{verkauf.v_menge + ' ' + verkauf.v_einheit}</TableCell>
                                        <TableCell>{verkauf.v_preis}</TableCell>
                                        <TableCell>
                                          {verkauf.v_betrag}
                                          {verkauf.v_betrag !== undefined && verkauf.v_betrag !== null && verkauf.v_betrag !== "" ? " €" : ""}
                                        </TableCell>
                                        <TableCell>{verkauf.v_bezahlt ? "Ja" : "Nein"}</TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  {modus === "kunden" && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={tableColumnCount}>
                        <Collapse in={expandedOrders === person.Id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, ml: 4, bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                              <Typography variant="subtitle1">
                                Bestellungen ({(orderData[person.Id] || []).length})
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<Add />}
                                disabled={!person.Aktiv}
                                onClick={() => setOrderDialogCustomer(person)}
                              >
                                Neue Bestellung
                              </Button>
                            </Box>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Bestelldatum</TableCell>
                                  <TableCell>Bestellung</TableCell>
                                  <TableCell>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {detailStatus[`orders:${person.Id}`]?.loading || detailStatus[`orders:${person.Id}`]?.error ? <TableRow><TableCell colSpan={6}>{detailStatus[`orders:${person.Id}`]?.error ? <LoadError onRetry={() => loadOrders(person.Id)}>{detailStatus[`orders:${person.Id}`].error}</LoadError> : <LoadingIndicator label="Bestellungen werden geladen …" compact />}</TableCell></TableRow> : (orderData[person.Id] || []).length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={3} align="center">
                                      Keine Bestellungen vorhanden.
                                    </TableCell>
                                  </TableRow>
                                ) : (orderData[person.Id] || []).map((order) => (
                                  <TableRow key={order.Id}>
                                    <TableCell>
                                      {order.Bestelldatum
                                        ? dayjs(order.Bestelldatum).format("DD.MM.YYYY") : ""}
                                    </TableCell>
                                    <TableCell>{order.Bestellung}</TableCell>
                                    <TableCell>{order.Erledigt ? "erledigt" : "offen"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {roleDialog && <RoleAssignments role={roleDialog.role} person={roleDialog.person} onClose={() => setRoleDialog(null)} onSaved={() => { setRevision(x => x + 1); setRoleDialog(null); triggerSnackbar("Zuordnung erfolgreich gespeichert.", "success"); }} />}
      <PersonDialog
          open={open}
          onClose={() => {
            setOpen(false);
            setSelectedPerson(null);
          }}
          onSave={handleSave} person={selectedPerson}
        />
        <BestellungDialog
          open={Boolean(orderDialogCustomer)}
          customer={orderDialogCustomer}
          onClose={() => setOrderDialogCustomer(null)}
          onSaved={() => {
            if (orderDialogCustomer) loadOrders(orderDialogCustomer.Id);
          }}
        />
        {/* Snackbar-Komponente */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbarSeverity}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
      {/* Detailbereich */}
      <Box sx={{ minWidth: 300, maxWidth: 400, bgcolor: "#f5f5f5", p: 2, borderRadius: 2, height: "fit-content" }}>
        {activePerson ? (
          <>
            <Typography variant="h6" gutterBottom>Personendetails</Typography>
            <Typography><b>ID:</b> {activePerson.Id}</Typography>
            <Typography><b>Name:</b> {activePerson.Typ === "ORGANIZATION" ? activePerson.Organisationsname : activePerson.Name}</Typography>
            <Typography><b>Vorname:</b> {activePerson.Vorname}</Typography>
            <Typography><b>Geburtstag:</b> {formatDetailDate(activePerson.Geburtsdatum)}</Typography>
            {modus === "jaeger" && <>
              <Typography><b>Eintrittsdatum:</b> {formatDetailDate(activePerson.Eintrittsdatum)}</Typography>
              <Typography><b>Austrittsdatum:</b> {formatDetailDate(activePerson.Austrittsdatum)}</Typography>
              <Typography><b>Status:</b> {activePerson.Status || "-"}</Typography>
              <Typography><b>Bereich:</b> {activePerson.Bereich ?? "-"}</Typography>
            </>}
            {modus === "kunden" && <Typography><b>Status:</b> {activePerson.Aktiv ? "aktiv" : "inaktiv"}</Typography>}
            <Typography><b>Straße / Hausnummer:</b> {activePerson.Strassenr || "-"}</Typography>
            <Typography><b>PLZ:</b> {activePerson.Plz || "-"}</Typography>
            <Typography><b>Ort:</b> {activePerson.Ort || "-"}</Typography>
            <Typography><b>Telefon 1:</b> {activePerson.Telefon1 || "-"}</Typography>
            <Typography><b>Telefon 2:</b> {activePerson.Telefon2 || "-"}</Typography>
            <Typography><b>Mail:</b> {activePerson.Mail || "-"}</Typography>
            <Typography><b>IBAN:</b> {activePerson.Iban || "-"}</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}><b>Bemerkungen:</b> {activePerson.Bemerkungen || "-"}</Typography>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Bitte wählen Sie einen Eintrag aus der Liste aus.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Personen;
